import { AlertBar } from '#GameFrontend/components/ui/AlertBar/AlertBar';
import { Button } from '#GameFrontend/components/ui/button';
import { Checkbox } from '#GameFrontend/components/ui/checkbox';
import { Input } from '#GameFrontend/components/ui/input';
import { Label } from '#GameFrontend/components/ui/label';
import { useLoading } from '#GameFrontend/contexts/LoadingContext';
import { socket } from '#GameFrontend/services/socket';
import { generateFilenameByGene } from '#GameFrontend/unauthorized/lib/GeneSelecter';
import { RuleSet, validate } from '#GameFrontend/unauthorized/lib/Validation';
import { Starter } from '#GameFrontend/unauthorized/types/Starter';
import { StarterFormProps } from '#GameFrontend/unauthorized/types/StarterForm';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
interface Step3Props {
	bloodline: Starter;
	nextStep: () => void;
	formData: StarterFormProps;
	setFormData: (data: StarterFormProps) => void;
}

const Step3: React.FC<Step3Props> = ({ bloodline, formData, setFormData, nextStep }) => {
	const { t } = useTranslation();
	const { isLoading, setIsLoading } = useLoading();
	const [isAlertVisible, setIsAlertVisible] = useState(true);
	const path = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORT !== '80' && process.env.REACT_APP_PORT !== '443' ? `:${process.env.REACT_APP_PORT}` : ''}/image/horses/starter/`;
	const filename = generateFilenameByGene(formData.genes, formData.horseIsMale);
	const [imagePath, setImagePath] = useState<string>(`${path}${bloodline.id}/${filename}.png`);
	const ruleSet = useRef<{ [key: string]: RuleSet } | undefined>(undefined);
	const [error, setError] = useState<string>('');
	const [registrationWithKey, setRegistrationWithKey] = useState<boolean>(false);

	const handleImageLoad = () => {
		setIsLoading(false);
	};

	const handleImageError = () => {
		setIsLoading(false);
	};

	const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.toUpperCase();

		// Format the key as it's typed
		const formattedKey =
			value
				.replace(/[^A-Z0-9]/g, '')
				.match(/.{1,5}/g)
				?.join('-') || value;

		// Update parent form data
		setFormData({
			...formData,
			registrationKey: formattedKey,
		});

		// Clear error when input changes
		setError('');
	};

	const handleNext = () => {
		if (!ruleSet.current) {
			setError(t('load.config.error'));
			return;
		}
		const errorList = [];

		if (!formData.terms) {
			errorList.push(t('validation.terms'));
		}
		if (!formData.wipe) {
			errorList.push(t('validation.wipe'));
		}

		const username = validate('username', ruleSet.current.username, formData.username);
		if (!username.valid) {
			errorList.push(username.message);
		}
		const email = validate('email', ruleSet.current.email, formData.email);
		if (!email.valid) {
			errorList.push(email.message);
		}
		const password = validate('password', ruleSet.current.password, formData.password);
		if (!password.valid) {
			errorList.push(password.message);
		}

		if (registrationWithKey) {
			const registrationKey = validate(
				'Registration Key',
				ruleSet.current.registrationKey,
				formData.registrationKey || ''
			);
			if (!registrationKey.valid) {
				errorList.push(registrationKey.message);
			}
		}

		if (errorList.length > 0) {
			setError(errorList.join('\n'));
			return;
		}

		const updatedFormData = {
			...formData,
			id: bloodline.id,
		};

		setIsLoading(true);
		socket.emit('User:Create', updatedFormData);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type } = e.target;
		setFormData({ ...formData, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value });
	};

	useEffect(() => {
		socket.timeout(5000).emit('User:Create:ValidationConfig', {});
		socket.emit('RegistrationStateConfig:Get');
	}, [imagePath]);

	// useEffect(() => {

	// }, [registrationWithKey])
	useEffect(() => {
		socket.on('RegistrationStateConfig:Get', (data) => {
			if (data.success) {
				setRegistrationWithKey(data.data.registrationWithKey);
			}
		});

		socket.on('User:Create:ValidationConfig', (data) => {
			setIsLoading(false);
			if (data.success) {
				ruleSet.current = data.data as { [key: string]: RuleSet };
			} else {
				setError(t('load.config.error'));
			}
		});
		socket.on('User:Create', (data) => {
			setIsLoading(false);
			if (data.success) {
				nextStep();
			} else {
				setIsAlertVisible(true);
				if (data.translation?.length > 0) {
					const translation = data.translation.map(
						(translation: { key: string; params: Record<string, string> }) =>
							t(translation.key, translation.params)
					);
					setError(translation.join('\n'));
				} else {
					setError(data.message || t('validation.email.failed'));
				}
			}
		});
	}, []);

	return (
		<>
			{error && (
				<AlertBar type="error" message={error} isVisible={isAlertVisible} setIsVisible={setIsAlertVisible} />
			)}
			<div className={`lg:py-6 lg:pl-6 xl:pl-0 flex-col flex-grow text-white text-center hidden lg:flex`}>
				<h2 className="text-4xl text-white mb-0 lg:mb-4 text-center drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] relative z-30">
					<div className="flex items-center justify-center">
						<svg
							width="65%"
							height="70"
							xmlns="http://www.w3.org/2000/svg"
							className="lg:hidden text-center inline-block"
						>
							<text x="50%" y="50" className="outlined-text svg-text font-lobster" textAnchor="middle">
								{formData.horseName}
							</text>
						</svg>
						<svg
							width={formData.horseName.length * 24}
							height="40"
							xmlns="http://www.w3.org/2000/svg"
							className="hidden lg:inline-block text-center overflow-visible p-1"
						>
							<text x="50%" y="30" className="outlined-text svg-text font-lobster" textAnchor="middle">
								{formData.horseName}
							</text>
						</svg>
						<div
							className={`sprite-46 active ${formData.horseIsMale ? 'male' : 'female'} cursor-pointer min-w-[42px]`}
						></div>
					</div>
				</h2>
				<img
					key={imagePath}
					src={imagePath}
					alt="Horse"
					className="object-contain w-full h-full"
					onLoad={handleImageLoad}
					onError={handleImageError}
				/>
			</div>
			<div className={`lg:pl-12 py-6 lg:pt-2 xl:p-6  flex-col flex-grow text-white`}>
				<div className="flex flex-grow flex-col">
					<h2 className="text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] hidden lg:inline-block">
						<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg" className="overflow-visible">
							<text x="10" y="50" className="outlined-text  svg-text-sm">
								{t('register.info.title')}
							</text>
						</svg>
					</h2>
					<div className="flex flex-col">
						<form
							className="relative"
							onSubmit={(e) => {
								e.preventDefault();
								handleNext();
							}}
						>
							<div className="wood-light p-6 pb-6 rounded-[15px] shadow-md lg:mt-4">
								<div className="space-y-2 relative">
									<Label htmlFor="username" className="text-white text-lg">
										{t('username')}
									</Label>
									<Input
										id="username"
										type="text"
										name="username"
										value={formData.username}
										onChange={handleInputChange}
										className="bg-white text-brown"
									/>
								</div>
								<div className="space-y-2 relative mt-2">
									<Label htmlFor="email" className="text-white text-lg">
										{t('email')}
									</Label>
									<Input
										id="email"
										type="text"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										className="bg-white text-brown"
									/>
								</div>
								<div className="space-y-2 relative mt-2">
									<Label htmlFor="password" className="text-white text-lg">
										{t('password')}
									</Label>
									<Input
										id="password"
										type="password"
										name="password"
										value={formData.password}
										onChange={handleInputChange}
										className="bg-white text-brown"
									/>
								</div>
								{registrationWithKey && (
									<div className="space-y-2 relative mt-2">
										<Label htmlFor="registrationKey" className="text-white text-lg">
											{t('register.info.key')}
										</Label>
										<Input
											id="registrationKey"
											type="text"
											value={formData.registrationKey || ''}
											onChange={handleKeyChange}
											placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
											className="bg-white text-brown"
											maxLength={23}
										/>
									</div>
								)}
								<div className="space-y-2 relative mt-2 flex items-center justify-center">
									<Checkbox
										id="terms"
										name="terms"
										checked={formData.terms}
										onCheckedChange={(checked) => {
											handleInputChange({
												target: {
													name: 'terms',
													checked,
													type: 'checkbox',
												},
											} as React.ChangeEvent<HTMLInputElement>);
										}}
										className={`w-5 h-5 mr-2  cursor-pointer border-none data-[state=checked]:bg-[#fb923c] bg-white`}
									/>
									<Label htmlFor="terms" className="cursor-pointer">
										<Trans
											i18nKey="register.info.terms.text"
											components={{
												Link: <Link to="/terms" className="text-[#fb923c] hover:underline" />,
											}}
										>
											{'I have read and accept the <Link>terms and conditions</Link>'}
										</Trans>
									</Label>
								</div>
								<div className="space-y-2 relative mt-2 flex items-center justify-center">
									<Checkbox
										id="wipe"
										name="wipe"
										checked={formData.wipe}
										onCheckedChange={(checked) => {
											handleInputChange({
												target: {
													name: 'wipe',
													checked,
													type: 'checkbox',
												},
											} as React.ChangeEvent<HTMLInputElement>);
										}}
										className={`w-5 h-5 mr-2  cursor-pointer border-none data-[state=checked]:bg-[#fb923c] bg-white`}
									/>
									<Label htmlFor="wipe" className="cursor-pointer">
										<Trans
											i18nKey="register.info.wipe.text"
											components={{
												span: <span className="text-[#fb923c]" />,
											}}
										>
											{
												'I know that this is an <span>ALPHA</span> version and my data might be wiped'
											}
										</Trans>
									</Label>
								</div>

								<div className="flex justify-center lg:hidden space-y-2 mt-4 relative">
									<Button
										type="submit"
										className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md text-lg"
									>
										{isLoading ? t('register.signup.loading') : t('register.signup')}
									</Button>
								</div>
							</div>
							<div className="hidden lg:flex justify-center mt-0 pt-0">
								<Button
									type="submit"
									className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md rounded-t-none border-t-0"
								>
									{isLoading ? t('register.signup.loading') : t('register.signup')}
								</Button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</>
	);
};
export default Step3;
